import type { ShearMode, SoilType, TreatmentMethod } from "../domain/types";
import { WATER_UNIT_WEIGHT } from "./settlement";

export interface SlopeLayer {
  type: SoilType;
  depthTop: number;
  depthBottom: number;
  gamma: number;
  gammaSat: number;
  c?: number;
  phi?: number;
  U_tConst?: number;
  deltaSigmaPreload?: number;
  deltaSigmaZ?: number;
}

export interface SlopeSearchInput {
  embankment: { height: number; width: number; slope: number; gamma: number };
  foundation: { surcharge: number; gwt: number; shearMode: ShearMode };
  treatment: TreatmentMethod;
  cdm: {
    diameter: number;
    spacing: number;
    pattern: "triangular" | "square";
    depth: number;
    qu: number;
    phi?: number;
    overWidth?: number;
  };
  layers: SlopeLayer[];
  useConsolidation: boolean;
  coarseSteps?: number;
  refineSteps?: number;
  slices?: number;
}

export interface CircleResult {
  Xc: number;
  Zc: number;
  R: number;
  FS: number;
}

interface Candidate { Xc: number; Dc: number; R: number; FS: number }

function surfaceDepth(x: number, input: SlopeSearchInput): number {
  const half = input.embankment.width / 2;
  const toe = half + input.embankment.slope * input.embankment.height;
  const absX = Math.abs(x);
  if (absX <= half) return -input.embankment.height;
  if (absX <= toe) return -input.embankment.height + (absX - half) / input.embankment.slope;
  return 0;
}

function columnLayoutMax(toeX: number, spacing: number): number {
  const count = Math.ceil((2 * toeX) / spacing);
  if (count % 2 === 0) return (count / 2) * spacing;
  return ((count + 1) / 2 - 0.5) * spacing;
}

export function calculateCircleSafetyFactor(
  candidate: Pick<Candidate, "Xc" | "Dc" | "R">,
  input: SlopeSearchInput,
): number {
  const { Xc, Dc, R } = candidate;
  if (Dc + R < 2) return 999;
  const { embankment, foundation, cdm, layers } = input;
  const half = embankment.width / 2;
  const toeX = half + embankment.slope * embankment.height;
  const cdmAreaRatio = cdm.pattern === "triangular"
    ? (Math.PI * cdm.diameter ** 2) / (2 * Math.sqrt(3) * cdm.spacing ** 2)
    : (Math.PI * cdm.diameter ** 2) / (4 * cdm.spacing ** 2);
  const cdmCohesion = (cdm.qu * 1000) / 2;
  const cdmMaxX = columnLayoutMax(toeX, cdm.spacing);

  const xLeft = Xc - R;
  const xRight = Xc + R;
  let xStart: number | null = null;
  let xEnd: number | null = null;
  const scanSteps = 80;
  for (let index = 0; index <= scanSteps; index += 1) {
    const x = xLeft + ((xRight - xLeft) * index) / scanSteps;
    const radicand = R ** 2 - (x - Xc) ** 2;
    if (radicand < 0) continue;
    if (Dc + Math.sqrt(radicand) > surfaceDepth(x, input)) {
      xStart ??= x;
      xEnd = x;
    }
  }
  const weakDepth = Math.max(0, ...layers.filter((layer) => layer.type === "cohesive").map((layer) => layer.depthBottom));
  const soilDepth = weakDepth || Math.max(0, ...layers.map((layer) => layer.depthBottom));
  if (xStart === null || xEnd === null || xEnd - xStart < 0.5) return 0;
  if (xEnd > toeX + Math.max(0.5 * soilDepth, 0.8 * embankment.height)) return 999;

  const sliceCount = input.slices ?? 20;
  const sliceWidth = (xEnd - xStart) / sliceCount;
  let drivingSum = 0;
  let resistingSum = 0;
  for (let index = 0; index < sliceCount; index += 1) {
    const x = xStart + (index + 0.5) * sliceWidth;
    const radicand = R ** 2 - (x - Xc) ** 2;
    if (radicand < 0) continue;
    const baseDepth = Dc + Math.sqrt(radicand);
    const topDepth = surfaceDepth(x, input);
    if (baseDepth <= topDepth) continue;
    const sinAlpha = Math.max(-1, Math.min(1, (Xc - x) / R));
    const alpha = Math.asin(sinAlpha);
    const cosAlpha = Math.cos(alpha);
    const baseLength = sliceWidth / cosAlpha;
    let weight = 0;
    if (topDepth < 0) {
      const fillHeight = Math.min(0, baseDepth) - topDepth;
      if (fillHeight > 0) weight += embankment.gamma * fillHeight * sliceWidth;
    }
    if (topDepth <= -embankment.height + 0.01 && Math.abs(x) <= half) weight += foundation.surcharge * sliceWidth;
    if (baseDepth > 0) {
      const soilTop = Math.max(0, topDepth);
      for (const layer of layers) {
        const layerTop = Math.max(soilTop, layer.depthTop);
        const layerBottom = Math.min(baseDepth, layer.depthBottom);
        if (layerBottom <= layerTop) continue;
        const midpoint = (layerTop + layerBottom) / 2;
        weight += (midpoint >= foundation.gwt ? layer.gammaSat : layer.gamma) * (layerBottom - layerTop) * sliceWidth;
      }
    }

    let cohesion = 10;
    let friction = 5;
    if (baseDepth > 0) {
      const baseLayer = layers.find((layer) => baseDepth >= layer.depthTop && baseDepth <= layer.depthBottom + 0.01);
      if (baseLayer) {
        cohesion = baseLayer.c ?? 10;
        friction = baseLayer.phi ?? 5;
        if (input.useConsolidation) {
          cohesion += (baseLayer.U_tConst ?? 0)
            * (baseLayer.deltaSigmaPreload ?? baseLayer.deltaSigmaZ ?? 0)
            * Math.tan((friction * Math.PI) / 180);
        }
      }
      if (input.treatment === "cdm" && baseDepth <= cdm.depth && Math.abs(x) <= cdmMaxX) {
        cohesion = cdmAreaRatio * cdmCohesion + (1 - cdmAreaRatio) * cohesion;
        friction = (Math.atan(
          cdmAreaRatio * Math.tan(((cdm.phi ?? 30) * Math.PI) / 180)
          + (1 - cdmAreaRatio) * Math.tan((friction * Math.PI) / 180),
        ) * 180) / Math.PI;
      }
    } else {
      cohesion = 5;
      friction = 30;
    }
    const undrained = foundation.shearMode === "undrained" && baseDepth > 0;
    const poreWaterForce = !undrained && baseDepth > foundation.gwt
      ? WATER_UNIT_WEIGHT * (baseDepth - foundation.gwt) * baseLength
      : 0;
    const effectiveNormal = Math.max(0, weight * cosAlpha - poreWaterForce);
    drivingSum += weight * Math.sin(alpha);
    resistingSum += cohesion * baseLength + effectiveNormal * Math.tan(((undrained ? 0 : friction) * Math.PI) / 180);
  }
  return drivingSum <= 0 ? 999 : resistingSum / drivingSum;
}

export function searchCriticalCircle(input: SlopeSearchInput): CircleResult | null {
  const totalDepth = Math.max(0, ...input.layers.map((layer) => layer.depthBottom));
  const half = input.embankment.width / 2;
  const toeX = half + input.embankment.slope * input.embankment.height;
  const xMin = half * 0.3;
  const xMax = toeX + input.embankment.height * 2;
  const dMin = -input.embankment.height * 4;
  const dMax = -input.embankment.height * 0.5;
  const coarseSteps = input.coarseSteps ?? 18;
  let best: Candidate | null = null;
  for (let i = 0; i <= coarseSteps; i += 1) {
    const Xc = xMin + ((xMax - xMin) * i) / coarseSteps;
    for (let j = 0; j <= coarseSteps; j += 1) {
      const Dc = dMin + ((dMax - dMin) * j) / coarseSteps;
      const radiusMin = Math.abs(Dc) + 2;
      const radiusMax = Math.abs(Dc) + Math.min(totalDepth, input.embankment.height * 1.8);
      if (radiusMin >= radiusMax) continue;
      for (let k = 0; k <= coarseSteps; k += 1) {
        const R = radiusMin + ((radiusMax - radiusMin) * k) / coarseSteps;
        const FS = calculateCircleSafetyFactor({ Xc, Dc, R }, input);
        if (FS > 0.2 && (!best || FS < best.FS)) best = { Xc, Dc, R, FS };
      }
    }
  }
  if (!best) return null;
  const ref = best;
  const dx = (xMax - xMin) / coarseSteps;
  const dd = (dMax - dMin) / coarseSteps;
  const dr = (Math.abs(ref.Dc) + totalDepth - Math.abs(ref.Dc) * 0.8) / coarseSteps;
  const refineSteps = input.refineSteps ?? 10;
  for (let i = 0; i <= refineSteps; i += 1) {
    const Xc = ref.Xc - dx + (2 * dx * i) / refineSteps;
    for (let j = 0; j <= refineSteps; j += 1) {
      const Dc = ref.Dc - dd + (2 * dd * j) / refineSteps;
      if (Dc >= 0) continue;
      for (let k = 0; k <= refineSteps; k += 1) {
        const R = ref.R - dr + (2 * dr * k) / refineSteps;
        if (R <= Math.abs(Dc) * 0.3) continue;
        const FS = calculateCircleSafetyFactor({ Xc, Dc, R }, input);
        if (FS > 0.2 && FS < best.FS) best = { Xc, Dc, R, FS };
      }
    }
  }
  return { Xc: best.Xc, Zc: -best.Dc, R: best.R, FS: best.FS };
}
